import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CornerOrnament from '../components/CornerOrnament';
import SEO from '../components/SEO';
import { useContent } from '../lib/content';
import Editable from '../components/Editable';
import API from '../lib/api';
import { toast } from 'sonner';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [siteInfo, setSiteInfo] = useState({});
  const { get } = useContent('contact');

  useEffect(() => {
    API.get('/site-settings').then(r => setSiteInfo(r.data?.site || {})).catch(() => {});
  }, []);

  const contactEmail = siteInfo.contact_email || 'info@plushresidence.ca';
  const contactPhone = siteInfo.phone || '+1 (416) 555-0100';
  const contactAddress = siteInfo.address || 'Ontario, Canada';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await API.post('/contact', form);
      toast.success(get('form_success_msg'));
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch { toast.error('Failed to send. Please try again.'); }
    setSending(false);
  };

  return (
    <div data-testid="contact-page" className="bg-[#101010]">
      <SEO
        title="Contact Us — Get in Touch"
        path="/contact"
        description="Contact Plush Residence for luxury short-term rental inquiries in Ontario."
        keywords="contact Plush Residence, luxury rental inquiry, book luxury rental Ontario"
      />
      <section className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-gold text-[11px] uppercase tracking-[0.4em] font-bold mb-5"><Editable page="contact" field="hero_label">{get('hero_label')}</Editable></motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Editable page="contact" field="hero_title" as="h1" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold mb-5">{get('hero_title')}</Editable>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Editable page="contact" field="hero_description" as="p" className="text-white/45 text-sm sm:text-base lg:text-lg max-w-xl mx-auto">{get('hero_description')}</Editable>
          </motion.div>
        </div>
      </section>
      <section className="pb-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Editable page="contact" field="form_name_label" as="label" className="text-[10px] uppercase tracking-widest text-white/45 mb-1.5 block font-medium">{get('form_name_label')}</Editable>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="bg-[#1c1c1c] border-white/[0.10] rounded-xl h-12 text-white placeholder:text-white/30" data-testid="contact-name" /></div>
                <div><Editable page="contact" field="form_email_label" as="label" className="text-[10px] uppercase tracking-widest text-white/45 mb-1.5 block font-medium">{get('form_email_label')}</Editable>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="bg-[#1c1c1c] border-white/[0.10] rounded-xl h-12 text-white placeholder:text-white/30" data-testid="contact-email" /></div>
              </div>
              <div><Editable page="contact" field="form_phone_label" as="label" className="text-[10px] uppercase tracking-widest text-white/45 mb-1.5 block font-medium">{get('form_phone_label')}</Editable>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-[#1c1c1c] border-white/[0.10] rounded-xl h-12 text-white placeholder:text-white/30" data-testid="contact-phone" /></div>
              <div><Editable page="contact" field="form_message_label" as="label" className="text-[10px] uppercase tracking-widest text-white/45 mb-1.5 block font-medium">{get('form_message_label')}</Editable>
                <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} required rows={5}
                  className="w-full bg-[#1c1c1c] border border-white/[0.10] rounded-xl p-4 text-sm text-white resize-none focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 placeholder:text-white/30" data-testid="contact-message" /></div>
              <Button type="submit" disabled={sending} className="bg-gold hover:bg-gold-dark text-white h-12 px-8 text-xs uppercase tracking-[0.2em] font-semibold rounded-full shadow-lg shadow-gold/20" data-testid="contact-submit">
                {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> <Editable page="contact" field="form_btn_text">{get('form_btn_text')}</Editable></>}
              </Button>
            </form>
          </div>
          <div className="lg:col-span-2 space-y-8">
            <div className="relative bg-[#1c1c1c] rounded-xl p-8 border border-white/[0.08]">
              <CornerOrnament size={16} offset="8px" />
              <Editable page="contact" field="info_heading" as="h3" className="font-heading text-lg font-semibold text-white mb-6">{get('info_heading')}</Editable>
              <div className="space-y-5">
                <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-gold mt-0.5" /><div><Editable page="contact" field="email_label" as="p" className="text-[10px] uppercase tracking-widest text-white/35 mb-0.5">{get('email_label')}</Editable><p className="text-sm text-white/50">{contactEmail}</p></div></div>
                <div className="flex items-start gap-3"><Phone className="w-5 h-5 text-gold mt-0.5" /><div><Editable page="contact" field="phone_label" as="p" className="text-[10px] uppercase tracking-widest text-white/35 mb-0.5">{get('phone_label')}</Editable><p className="text-sm text-white/50">{contactPhone}</p></div></div>
                <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-gold mt-0.5" /><div><Editable page="contact" field="office_label" as="p" className="text-[10px] uppercase tracking-widest text-white/35 mb-0.5">{get('office_label')}</Editable><p className="text-sm text-white/50">{contactAddress}</p></div></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
